import {
  ComponentPropsWithRef,
  ElementType,
  ReactNode,
  createElement,
} from 'react';
import { type VariantProps, tv } from 'tailwind-variants';

const overrides = (breakpoint: 'sm' | 'md' | 'lg' | 'xl') => ({
  // Studio:
  'studio/display/2xl': `${breakpoint}:text-[64px]/[72px]`,
  'studio/display/xl': `${breakpoint}:text-[36px]/[40px]`,
  'studio/title/xl': `${breakpoint}:text-[20px]/[24px]`,
  'studio/title/lg': `${breakpoint}:text-[16px]/[20px]`,
  'studio/title/md': `${breakpoint}:text-[14px]/[18px]`,
  'studio/subtitle': `${breakpoint}:text-[20px]/[24px]`,
  'studio/subtitle/medium': `${breakpoint}:text-[20px]/[24px]`,
  'studio/prompt/desktop': `${breakpoint}:text-[16px]/[20px]`,
  'studio/prompt/mobile': `${breakpoint}:text-[14px]/[18px]`,
  'studio/body/md': `${breakpoint}:text-[16px]/[20px]`,
  'studio/body/sm': `${breakpoint}:text-[14px]/[18px]`,
  'studio/body/xs': `${breakpoint}:text-[12px]/[16px]`,
  'studio/body/medium/24': `${breakpoint}:text-[24px]/[32px]`,
  'studio/body/medium/md': `${breakpoint}:text-[16px]/[20px]`,
  'studio/body/medium/sm': `${breakpoint}:text-[14px]/[18px]`,
  'studio/body/medium/xs': `${breakpoint}:text-[12px]/[16px]`,
  'studio/body/bold/md': `${breakpoint}:text-[16px]/[20px]`,
  'studio/body/bold/sm': `${breakpoint}:text-[14px]/[18px]`,
  'studio/body/bold/xs': `${breakpoint}:text-[12px]/[16px]`,
  // Homepage:
  'web/display/3xl': `${breakpoint}:text-[64px]/[72px]`,
  'web/display/2xl': `${breakpoint}:text-[40px]/[48px]`,
  'web/display/xl': `${breakpoint}:text-[32px]/[36px]`,
  'web/display/lg': `${breakpoint}:text-[20px]/[24px]`,
  'web/title/lg': `${breakpoint}:text-[20px]/[24px]`,
  'web/body/md': `${breakpoint}:text-[16px]/[20px]`,
  'web/body/24': `${breakpoint}:text-[24px]/[30px]`,
  'web/body/medium/xl': `${breakpoint}:text-[36px]/[40px]`,
  'web/body/medium/24': `${breakpoint}:text-[24px]/[32px]`,
  'web/body/medium/lg': `${breakpoint}:text-[20px]/[24px]`,
  'web/body/medium/md': `${breakpoint}:text-[16px]/[20px]`,
  'web/body/medium/xs': `${breakpoint}:text-[12px]/[16px]`,
});

/**
 * All variant names have to correspond to Figma.
 * Link: https://www.figma.com/design/wliJqoOEfpOGV4ggxUfqly/--UX---UI---Cartwheel-Component-Library?node-id=346-772&t=nu9AdGcyfROryeOI-0
 *
 * Please ask Steven before you add a new variant.
 *
 */
const text = tv(
  {
    variants: {
      variant: {
        // Studio:
        'studio/display/2xl':
          'font-display text-[64px]/[72px] font-bold tracking-tighter',
        'studio/display/xl':
          'font-display text-[36px]/[40px] font-bold tracking-tighter',
        'studio/title/xl':
          'font-display text-[20px]/[24px] font-medium tracking-tighter',
        'studio/title/lg':
          'font-display text-[16px]/[20px] font-medium tracking-tighter',
        'studio/title/md':
          'font-display text-[14px]/[18px] font-medium tracking-tighter',
        'studio/subtitle': 'font-sans text-[20px]/[24px] font-normal',
        'studio/subtitle/medium': 'font-sans text-[20px]/[24px] font-medium',
        'studio/prompt/desktop':
          'font-display text-[16px]/[20px] font-normal tracking-tighter',
        'studio/prompt/mobile':
          'font-display text-[14px]/[18px] font-normal tracking-tighter',
        'studio/body/md': 'font-sans text-[16px]/[20px] font-normal',
        'studio/body/sm': 'font-sans text-[14px]/[18px] font-normal',
        'studio/body/xs': 'font-sans text-[12px]/[16px] font-normal',
        'studio/body/medium/24': 'font-sans text-[24px]/[28px] font-medium',
        'studio/body/medium/md': 'font-sans text-[16px]/[20px] font-medium',
        'studio/body/medium/sm': 'font-sans text-[14px]/[18px] font-medium',
        'studio/body/medium/xs': 'font-sans text-[12px]/[16px] font-medium',
        'studio/body/bold/md': 'font-sans text-[16px]/[20px] font-bold',
        'studio/body/bold/sm': 'font-sans text-[14px]/[18px] font-bold',
        'studio/body/bold/xs': 'font-sans text-[12px]/[16px] font-bold',
        // Homepage:
        'web/display/3xl':
          'font-display text-[64px]/[72px] font-bold tracking-tighter',
        'web/display/2xl':
          'font-display text-[40px]/[48px] font-bold tracking-tighter',
        'web/display/xl':
          'font-display text-[32px]/[36px] font-bold tracking-tighter',
        'web/display/lg':
          'font-display text-[20px]/[24px] font-bold tracking-tighter',
        'web/title/lg': 'font-display text-[20px]/[24px] font-medium',
        'web/body/24': 'font-sans text-[24px]/[30px] font-normal',
        'web/body/md': 'font-sans text-[16px]/[20px] font-normal',
        'web/body/medium/xl': 'font-sans text-[36px]/[40px] font-medium',
        'web/body/medium/24': 'font-sans text-[24px]/[28px] font-medium',
        'web/body/medium/lg': 'font-sans text-[20px]/[24px] font-medium',
        'web/body/medium/md': 'font-sans text-[16px]/[20px] font-medium',
        'web/body/medium/xs': 'font-sans text-[12px]/[16px] font-medium',
      },
      'sm:variant': overrides('sm'),
      'md:variant': overrides('md'),
      'lg:variant': overrides('lg'),
      'xl:variant': overrides('xl'),
    },
    defaultVariants: {
      variant: 'studio/body/md',
    },
  },
  {
    twMerge: false,
  },
);

const defaultElement = 'span';

export type TextVariant = VariantProps<typeof text>['variant'];

type TextBaseProps = {
  variant: VariantProps<typeof text>['variant'];
  'sm:variant'?: TextVariant;
  'md:variant'?: TextVariant;
  'lg:variant'?: TextVariant;
  'xl:variant'?: TextVariant;
  className?: string;
  children?: ReactNode;
};

export type TextProps<C extends ElementType = typeof defaultElement> =
  TextBaseProps & {
    as?: C;
  } & Omit<ComponentPropsWithRef<C>, keyof TextBaseProps>;

export default function Text<C extends ElementType = typeof defaultElement>({
  as,
  children,
  variant,
  'sm:variant': sm,
  'md:variant': md,
  'lg:variant': lg,
  'xl:variant': xl,
  className,
  ...restProps
}: TextProps<C>) {
  const Component = as || defaultElement;
  const classNameValue = text({
    variant,
    'sm:variant': sm,
    'md:variant': md,
    'lg:variant': lg,
    'xl:variant': xl,
    className,
  });

  // Using createElement due to some type issues with the defaultElement...
  return createElement(
    Component,
    {
      className: classNameValue,
      ...restProps,
    },
    children,
  );
}
